import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  TextField,
  Box,
  Button,
  AppBar,
  Container,
  Modal,
  Toolbar,
  Typography,
  Paper,
  Divider,
  Tooltip,
  IconButton,
} from '@mui/material';
import { useNavigate } from "react-router-dom";
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import { Info as InfoIcon } from '@mui/icons-material';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const EditConcession = ({ onSave, onCancel }) => {
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  let navigate = useNavigate();
  const [concessionName, setConcessionName] = useState('');
  const [remark, setRemark] = useState('');
  const { concessionId } = useParams();
  const [loadingBarProgress, setLoadingBarProgress] = useState('');

  // Info modal state
  const [showInfoModal, setShowInfoModal] = useState(false);

  const handleUpdate = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      setLoadingBarProgress(30);
      const response = await fetch(`${apiUrl}/Concession/Id?Id=${concessionId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: sessionStorage.getItem('token'),
        },
        body: JSON.stringify({
          concessionName: concessionName,
          remark: remark,
        }),
      });

      if (response.ok) {
        const responseData = await response.json();
        setLoadingBarProgress(100);
        setShowSuccessModal(true);
      } else {
        setLoadingBarProgress(0);
        // alert('Unable to update concession');
        setSnackbarMessage('Unable to update concession');
        setOpenSnackbar(true);
        setSnackbarSeverity("error");
      }
    } catch (error) {
      console.error('API request error:', error);
      // alert('An error occurred. Please try again later.');
      setSnackbarMessage('An error occurred. Please try again later.');
      setOpenSnackbar(true);
      setSnackbarSeverity("error");
    }
  };

  const handleCancel = () => {
    // onCancel();
    // setConcessionName('');
    // setRemark('');
    navigate(-1);
  };

  useEffect(() => {
    const fetchConcessionData = async () => {
      try {
        setLoadingBarProgress(30);
        const token = sessionStorage.getItem('token');
        const apiUrl = process.env.REACT_APP_BASE_URL;
        const response = await fetch(
          `${apiUrl}/Concession/GetConcession`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: token,
            },
            body: JSON.stringify({}),
          }
        );

        if (response.ok) {
          const responseData = await response.json();
          const concession = responseData.find((concession) => concession.concessionId === parseInt(concessionId));
          if (concession) {
            setConcessionName(concession.concessionName);
            setRemark(concession.remark);
          } else {
            console.error('Concession not found for the given ID');
            // alert('Concession not found');
            setSnackbarMessage('Concession not found');
            setOpenSnackbar(true);
            setSnackbarSeverity("error");
          }
          setLoadingBarProgress(100);
        } else {
          console.error('Failed to fetch concession data');
          // alert('Failed to fetch concession data');
          setSnackbarMessage('Failed to fetch concession data');
          setOpenSnackbar(true);
          setSnackbarSeverity("error");
          setLoadingBarProgress(0);
        }
      } catch (error) {
        console.error('API request error:', error);
        // alert('An error occurred. Please try again later.');
        setSnackbarMessage('An error occurred. Please try again later.');
        setOpenSnackbar(true);
        setSnackbarSeverity("error");
      }
    };

    fetchConcessionData(); // Fetch concession data when the component mounts
  }, [concessionId]);

  // Info sections data
  const infoSections = [
    {
      title: "📋 Page Overview",
      content:
        "The Edit Concession page allows authorized staff to update an existing fee concession record. The current concession details are pre-loaded into the form. Make the required changes and click Update to save.",
    },
    {
      title: "📝 Form Fields",
      items: [
        "Concession Name – The name of the concession (e.g., Sibling Discount, Merit-Based, Staff Concession). Pre-filled with the existing value.",
        "Remark – An optional description or note about the concession. Pre-filled with the existing value if available.",
      ],
    },
    {
      title: "⚙️ Actions",
      items: [
        "Update – Saves the changes made to the concession record. Shows a success dialog on completion or an error message if the operation fails.",
        "Cancel – Discards any changes and navigates back to the previous page without saving.",
      ],
    },
    {
      title: "✅ After Updating",
      content:
        "A confirmation dialog appears confirming the concession was updated successfully. Click OK to close the dialog and return to the previous page.",
    },
  ];

  return (
    <Container mt={4}>
      <AppBar position="static" style={{ backgroundColor: "#0B1F3D" }}>
        <Toolbar sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h4" component="div" style={{ fontWeight: 'bold' }}>
            Edit Concession
          </Typography>

          {/* Info Icon Button */}
          <Tooltip title="View page information & guide" arrow>
            <IconButton
              onClick={() => setShowInfoModal(true)}
              size="small"
              sx={{
                color: "rgba(255,255,255,0.75)",
                backgroundColor: "rgba(255,255,255,0.1)",
                border: "1.5px solid rgba(255,255,255,0.25)",
                borderRadius: "50%",
                width: 30,
                height: 30,
                ml: 0.5,
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: "rgba(100,181,246,0.25)",
                  borderColor: "#64B5F6",
                  color: "#64B5F6",
                  transform: "scale(1.1)",
                },
              }}
            >
              <InfoIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      <Paper elevation={3} style={{ padding: 16, width: '100%', margin: 'auto', marginTop: 6 }}>
        <form>
          <Box marginBottom="20px">
            <TextField
              label="Concession Name"
              variant="outlined"
              value={concessionName}
              onChange={(e) => setConcessionName(e.target.value)}
              placeholder="Enter Concession Name"
              sx={{ width: '100%' }}
            />
          </Box>

          <Box marginBottom="20px">
            <TextField
              label="Remark"
              variant="outlined"
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              placeholder="Enter Remark"
              sx={{ width: '100%' }}
            />
          </Box>

          <Box>
        <Tooltip title={!concessionName.trim() ? "Enter concession name" : ""} arrow>
  <span>
    <Button
      variant="contained"
      color="primary"
      onClick={handleUpdate}
      disabled={!concessionName.trim()}
      style={{ marginRight: '10px' }}
    >
      Update
    </Button>
  </span>
</Tooltip>
            <Button variant="contained" color="error" onClick={handleCancel}>
              Cancel
            </Button>
          </Box>
        </form>
      </Paper>

      {/* ===== Info Modal ===== */}
      <Modal
        open={showInfoModal}
        onClose={() => setShowInfoModal(false)}
        aria-labelledby="info-modal-title"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '95%', sm: 520 },
            maxHeight: '85vh',
            bgcolor: 'background.paper',
            borderRadius: 3,
            boxShadow: '0 8px 40px rgba(11,31,61,0.22)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Modal Header */}
          <Box
            sx={{
              backgroundColor: '#0B1F3D',
              padding: '14px 20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <InfoIcon sx={{ color: '#64B5F6', fontSize: 22 }} />
              <Typography id="info-modal-title" variant="h6" sx={{ color: '#fff', fontWeight: 700 }}>
                Edit Concession — Page Guide
              </Typography>
            </Box>
            {/* Close Button */}
            <IconButton
              onClick={() => setShowInfoModal(false)}
              size="small"
              sx={{
                color: 'rgba(255,255,255,0.7)',
                '&:hover': { color: '#fff', backgroundColor: 'rgba(255,255,255,0.1)' },
              }}
            >
              ✕
            </IconButton>
          </Box>

          {/* Modal Body — scrollable */}
          <Box sx={{ overflowY: 'auto', padding: '20px 24px', flex: 1 }}>
            {infoSections.map((section, idx) => (
              <Box key={idx} sx={{ mb: 2.5 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 700,
                    color: '#0B1F3D',
                    mb: 0.8,
                    fontSize: '0.97rem',
                  }}
                >
                  {section.title}
                </Typography>

                {section.content && (
                  <Typography
                    variant="body2"
                    sx={{ color: '#444', lineHeight: 1.7, fontSize: '0.875rem' }}
                  >
                    {section.content}
                  </Typography>
                )}

                {section.items && (
                  <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                    {section.items.map((item, i) => (
                      <Box
                        component="li"
                        key={i}
                        sx={{
                          fontSize: '0.875rem',
                          color: '#444',
                          lineHeight: 1.75,
                          mb: 0.3,
                        }}
                      >
                        {item}
                      </Box>
                    ))}
                  </Box>
                )}

                {idx < infoSections.length - 1 && (
                  <Divider sx={{ mt: 2, borderColor: 'rgba(11,31,61,0.08)' }} />
                )}
              </Box>
            ))}
          </Box>

          {/* Modal Footer */}
          <Box
            sx={{
              padding: '12px 24px',
              borderTop: '1px solid rgba(11,31,61,0.1)',
              display: 'flex',
              justifyContent: 'flex-end',
              backgroundColor: '#f9fafb',
            }}
          >
            <Button
              variant="contained"
              onClick={() => setShowInfoModal(false)}
              sx={{
                backgroundColor: '#0B1F3D',
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': { backgroundColor: '#1a3560' },
              }}
            >
              Got it
            </Button>
          </Box>
        </Box>
      </Modal>

      <Modal
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        aria-labelledby="success-modal-title"
        aria-describedby="success-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Box sx={{ backgroundColor: '#0B1F40', padding: '12px 16px' }}>
            <Typography id="success-modal-title" variant="h6" sx={{ color: '#fff' }}>
              Concession Updated ✅
            </Typography>
          </Box>

          {/* Content */}
          <Box sx={{ padding: '20px' }}>
            <Typography id="success-modal-description" sx={{ mb: 2 }}>
              The Concession has been updated successfully.
            </Typography>

            {/* Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button
                onClick={() => {
                  setShowSuccessModal(false);
                  navigate(-1); // go back after closing modal
                }}
                variant="contained"
                color="primary"
              >
                OK
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

    </Container>
  );
};

export default EditConcession;